import { useMemo, useState, useEffect, useCallback } from "react";
import useTranslation from "@/hooks/use_translation";

import { SectionHeader } from "@/sprout/typography";
import Container from "@/components/ui/container";
import { SearchBar } from "@components/job_search/search_bar.tsx";
import { SingleSelectFilter } from "@components/job_search/filters/single_select_filter.tsx";
import { MultiSelectFilter } from "@components/job_search/filters/multi_select_filter.tsx";
import { router } from "@inertiajs/react";
import {
  useDatePostedOptions,
  useEmploymentTypeOptions,
  useSalaryOptions,
  useWorkTypeOptions,
} from "@hooks/job_search/use_filter_options";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount.ts";
import JobSearchTable from "@components/job_search/table.tsx";
import useDevice from "@hooks/use_device.ts";
import HelpPopover from "@sprout/help_popover.tsx";
import { TertiaryButton } from "@sprout/buttons";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";

export type JobPost = {
  id: string;
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl: string;
  firstPublished: string;
  location: string;
  payRanges: string;
};

export type SearchParams = {
  query?: string;
  location?: string;
  lat?: number;
  lon?: number;
  date_posted?: string;
  salary?: string;
  work_type?: string[];
  employment_type?: string[];
};

type Props = {
  jobPosts: JobPost[] | undefined;
  moreResultsAvailable: boolean;
  page: number;
  browsing: boolean;
  trackingData: { [key: string]: string };
};

const buildLocation = (urlParams: URLSearchParams) => {
  if (!urlParams.get("location") || !urlParams.get("lat") || !urlParams.get("lon")) {
    return null;
  }

  return {
    display_name: urlParams.get("location") || "",
    latitude: Number(urlParams.get("lat")) || 0,
    longitude: Number(urlParams.get("lon")) || 0,
  };
};

const JobSearch = ({ jobPosts, moreResultsAvailable, page, browsing, trackingData }: Props) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const [location, setLocation] = useState(buildLocation(urlSearchParams));
  const [query, setQuery] = useState(urlSearchParams.get("query") || "");
  const tracker = useSnowplowTracker();

  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const [loading, setLoading] = useState(false);
  const [datePosted, setDatePosted] = useState(urlSearchParams.get("date_posted") || "");
  const [salary, setSalary] = useState(urlSearchParams.get("salary") || "");
  const [workType, setWorkType] = useState<string[]>(urlSearchParams.getAll("work_type[]"));
  const [employmentType, setEmploymentType] = useState<string[]>(
    urlSearchParams.getAll("employment_type[]"),
  );
  const [searchResultsText, setSearchResultsText] = useState<string>(t("results"));
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");

  const datePostedOptions = useDatePostedOptions();
  const salaryOptions = useSalaryOptions();
  const workTypeOptions = useWorkTypeOptions();
  const employmentTypeOptions = useEmploymentTypeOptions();

  useEffect(() => {
    if (typeof jobPosts === "undefined") return;

    if (jobPosts.length > 0) {
      setSearchResultsText(t("results"));
      return;
    }

    if (query) {
      setSearchResultsText(t("noResultsForSearchTerm", { searchTerm: query }));
      setNoResultsMessage(t("noResultsForSearchTermMessage"));
      return;
    }

    setSearchResultsText(t("noResults"));
    setNoResultsMessage(t("noResultsMessage"));
  }, [jobPosts]);

  const resetFilters = () => {
    setDatePosted("");
    setSalary("");
    setWorkType([]);
    setEmploymentType([]);
  };

  const hasActiveFilters = useMemo(
    () => datePosted !== "" || salary !== "" || workType.length > 0 || employmentType.length > 0,
    [datePosted, salary, workType, employmentType],
  );

  const searchParams: SearchParams = {
    query: query || undefined,
    location: location?.display_name,
    lat: location?.latitude,
    lon: location?.longitude,
    date_posted: datePosted || undefined,
    salary: salary || undefined,
    work_type: workType || undefined,
    employment_type: employmentType || undefined,
  };

  const handleSearch = () => {
    setLoading(true);
    jobPosts = undefined;

    router.get("/jobs", searchParams, {
      onFinish: () => setLoading(false),
    });
  };

  useEffectAfterMount(() => {
    handleSearch();
  }, [datePosted, salary, workType, employmentType]);

  const createTrackSearchResultClick = useCallback(
    (postId: string, title: string) => {
      return () => {
        tracker.trackEvent(SnowplowEvent.SearchResultClicked, {
          job_post_id: postId,
          job_post_title: title,
          ...trackingData,
        });
      };
    },
    [trackingData],
  );

  return (
    <>
      <div className="py-9 pl-7 pr-6 bg-green-100">
        <SearchBar
          onSearch={handleSearch}
          query={query}
          onQueryChange={setQuery}
          location={location}
          onLocationChange={setLocation}
          disabled={loading}
        />
      </div>
      <div className="flex gap-3 mt-6 mb-3 pb-2 px-3 mx-auto text-nowrap overflow-y-auto hide-mobile-scrollbar max-w-fit items-center">
        <SingleSelectFilter
          name={t("datePosted")}
          options={datePostedOptions}
          value={datePosted}
          onValueChange={setDatePosted}
        />
        <SingleSelectFilter
          name={t("salary")}
          options={salaryOptions}
          value={salary}
          onValueChange={setSalary}
        />
        <MultiSelectFilter
          name={t("workType")}
          options={workTypeOptions}
          values={workType}
          onValuesChange={setWorkType}
        />
        <MultiSelectFilter
          name={t("employmentType")}
          options={employmentTypeOptions}
          values={employmentType}
          onValuesChange={setEmploymentType}
        />
        {hasActiveFilters && (
          <TertiaryButton disabled={!hasActiveFilters} secondary onClick={resetFilters}>
            {t("resetFilters")}
          </TertiaryButton>
        )}
      </div>
      <Container className="!max-w-[1550px] !pt-0">
        <div className="flex flex-row items-center gap-2">
          <SectionHeader>{searchResultsText}</SectionHeader>
          {device.isMobile && <HelpPopover text={t("aiDisclaimer")} />}
        </div>
        <JobSearchTable
          jobPosts={jobPosts}
          browsing={browsing}
          moreResultsAvailable={moreResultsAvailable}
          currentPage={page}
          lastPublishDate={jobPosts?.at(-1)?.firstPublished || null}
          emptyStateMessage={noResultsMessage}
          searchParams={searchParams}
          createTrackSearchResultClick={createTrackSearchResultClick}
        />
      </Container>
    </>
  );
};

export default JobSearch;
